import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  ADMIN › ELLENŐRZÉS › JEGYEK TANÁRONKÉNT (backend/jegy-ellenorzes.js › /tanar-jegyek).
 *
 *  A „Jegy nélküli tantárgyak” párja: nem azt mutatja, hol nincs jegy, hanem amit egy tanár
 *  ténylegesen beírt (a rögzítő tanár szerint, stornó nélkül). Tanár nélkül összesítő –
 *  tanáronként a jegyek és a tantárgyak száma, az utolsó jegy napja –, abból kattintva
 *  választ az admin. Tanárral: tantárgyanként a jegyek, darabszám és átlag, PDF és CSV.
 *
 *  AZ ÁTLAG csak az évközi érdemjegyekből (1–5) számol, az értékelés módjának súlyával
 *  (témazáró 2×, a 0× súlyú diagnosztikus mérés kimarad), a „nem írta” (–) helyfoglaló
 *  nélkül – ahogy a napló is. A százalékos értékelés külön átlagot kap; a szöveges nem számít.
 */
@Component({
  selector: 'app-admin-tanar-jegyek',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgSelectModule],
  templateUrl: './tanar-jegyek.component.html',
  styleUrls: ['./tanar-jegyek.component.scss'],
})
export class AdminTanarJegyekComponent implements OnInit {
  intezmeny = '';
  betolt = false;
  hiba = '';

  tol = '';
  ig = '';
  /** Az összesítő (minden tanár az időszakban) – a választó listája is. */
  tanarok: any[] = [];
  tanar: string | null = null;
  /** A kiválasztott tanár jegyei (a kiszolgáló válasza). */
  adat: any = null;
  tantargy = '';
  rendezes: 'nev' | 'jegyek' = 'nev';

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
    this.intezmeny = String(sajat?.intezmenyId || '');
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    await this.toltes();
  }

  private params(tanar?: string | null): any {
    const p: any = {};
    if (this.tol) p.tol = this.tol;
    if (this.ig) p.ig = this.ig;
    if (tanar) p.tanar = tanar;
    return p;
  }

  /** Az összesítő újratöltése (időszak-váltáskor), és ha van kiválasztott tanár, az ő jegyei. */
  async toltes() {
    this.betolt = true; this.hiba = '';
    try {
      const o: any = await firstValueFrom(this.http.get(`/api/jegy-ellenorzes/tanar-jegyek/${this.intezmeny}`, { params: this.params() }));
      this.tol = o?.idoszak?.tol || this.tol;
      this.ig = o?.idoszak?.ig || this.ig;
      this.tanarok = (o?.tanarok || []).map((t: any) => ({ ...t, cimke: `${t.nev} (${t.jegyek} jegy)` }));
      if (this.tanar) await this.tanarToltes(false);
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni az összesítőt.';
    } finally { this.betolt = false; }
  }

  async valaszt(adid: string | null) {
    this.tanar = adid || null;
    this.tantargy = '';
    this.adat = null; this.tantargyak = []; this.szures();
    if (this.tanar) await this.tanarToltes(true);
  }

  private async tanarToltes(sajatJelzo: boolean) {
    if (!this.tanar) return;
    if (sajatJelzo) { this.betolt = true; this.hiba = ''; }
    try {
      this.adat = await firstValueFrom(this.http.get(`/api/jegy-ellenorzes/tanar-jegyek/${this.intezmeny}`, { params: this.params(this.tanar) }));
      this.tantargyak = this.tantargyLista();
      if (this.tantargy && !this.tantargyak.some((t) => t.id === this.tantargy)) this.tantargy = '';
      this.szures();
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni a tanár jegyeit.';
    } finally { if (sajatJelzo) this.betolt = false; }
  }

  get osszesito(): any[] {
    const s = [...this.tanarok];
    return this.rendezes === 'jegyek' ? s.sort((a, b) => b.jegyek - a.jegyek || a.nev.localeCompare(b.nev, 'hu')) : s;
  }

  get osszesJegy(): number { return this.tanarok.reduce((a, t) => a + Number(t.jegyek || 0), 0); }

  /* ── A kiválasztott tanár jegyei ─────────────────────────────────────── */

  /** Érdemjegy (1–5) vagy null – a szöveges és a „nem írta” nem jegy. */
  private erdemjegy(j: any): number | null {
    if (Number(j.nem_irta)) return null;
    const s = String(j.ertekeles ?? '').trim();
    if (/^[1-5]$/.test(s)) return Number(s);
    const n = Number(j.ertek_szam);
    return j.ertek_forma === 'erdemjegy' && n >= 1 && n <= 5 ? n : null;
  }

  private szazalek(j: any): number | null {
    if (Number(j.nem_irta)) return null;
    const m = /^(\d{1,3}(?:[.,]\d+)?)\s*%$/.exec(String(j.ertekeles ?? '').trim());
    return m ? Number(m[1].replace(',', '.')) : null;
  }

  /** Évközi? (a félévi / év végi / negyedéves jegy az átlagba nem számít) */
  private evkozi(j: any): boolean { return j.tipus_id == null || Number(j.tipus_id) === 1; }

  private atlagok(jegyek: any[]): { atlag: number | null; szazalek: number | null; erdemjegyek: number } {
    let sum = 0; let suly = 0; let db = 0; let pSum = 0; let pDb = 0;
    for (const j of jegyek) {
      if (!this.evkozi(j)) continue;
      const w = j.suly == null ? 1 : Number(j.suly) / 100;
      const e = this.erdemjegy(j);
      if (e != null && w > 0) { sum += e * w; suly += w; db += 1; continue; }
      const p = this.szazalek(j);
      if (p != null) { pSum += p; pDb += 1; }
    }
    return { atlag: suly > 0 ? sum / suly : null, szazalek: pDb ? pSum / pDb : null, erdemjegyek: db };
  }

  /** Tantárgyanként: darab, tanulók, átlag, utolsó jegy – a szűrő és az összesítő tábla is ebből.
   *  Betöltéskor egyszer számol (nem getter: a változásérzékelés minden körében újraszámolná). */
  tantargyak: any[] = [];
  /** A megjelenített tantárgyak (a tantárgy-szűrő után) és az összesítésük. */
  csoportok: any[] = [];
  ossz: { db: number; atlag: number | null; szazalek: number | null } = { db: 0, atlag: null, szazalek: null };

  private tantargyLista(): any[] {
    const m = new Map<string, any>();
    for (const j of this.adat?.jegyek || []) {
      if (!m.has(j.tantargy_id)) m.set(j.tantargy_id, { id: j.tantargy_id, nev: j.tantargy, jegyek: [] as any[] });
      m.get(j.tantargy_id).jegyek.push(j);
    }
    return [...m.values()].map((t) => {
      const egysegek = new Set(t.jegyek.map((j: any) => this.egyseg(j)));
      return {
        ...t, ...this.atlagok(t.jegyek), db: t.jegyek.length,
        tanulok: new Set(t.jegyek.map((j: any) => j.oktazon)).size,
        egysegek: [...egysegek].sort((a: any, b: any) => String(a).localeCompare(String(b), 'hu', { numeric: true })).join(', '),
        utolso: t.jegyek.reduce((a: string, j: any) => (j.datum > a ? j.datum : a), ''),
      };
    }).sort((a, b) => a.nev.localeCompare(b.nev, 'hu'));
  }

  szures() {
    this.csoportok = this.tantargy ? this.tantargyak.filter((x) => x.id === this.tantargy) : this.tantargyak;
    const jegyek = this.csoportok.flatMap((t) => t.jegyek);
    const a = this.atlagok(jegyek);
    this.ossz = { db: jegyek.length, atlag: a.atlag, szazalek: a.szazalek };
  }

  egyseg(j: any): string { return j.csoport ? `${j.osztaly || ''} · ${j.csoport}` : (j.osztaly || ''); }
  jegySzoveg(j: any): string { return `${j.ertekeles ?? ''}${Number(j.dicseretes) ? ' (dicséretes)' : ''}`; }
  modSzoveg(j: any): string {
    const t = this.evkozi(j) ? '' : (j.tipus || '');
    return [t, j.modja || ''].filter(Boolean).join(' · ');
  }
  szam(v: number | null, tized = 2): string { return v == null ? '—' : v.toFixed(tized).replace('.', ','); }
  jegyOsztaly(j: any): string {
    const e = this.erdemjegy(j);
    return e == null ? '' : `j${e}`;
  }

  /* ── Kimenetek ──────────────────────────────────────────────────────── */

  private fajlTorzs(): string {
    const nev = String(this.adat?.tanar?.nev || 'tanar').replace(/[^\p{L}\p{N}]+/gu, '_');
    return `jegyek_${nev}_${this.adat?.idoszak?.tol || ''}_${this.adat?.idoszak?.ig || ''}`;
  }

  async pdf() {
    if (!this.adat) return;
    const tanarNev = this.adat.tanar?.nev || '';
    const idoszak = `${this.adat.idoszak?.tol} – ${this.adat.idoszak?.ig}`;
    const T = this.csoportok;
    const sorok: any[] = [];
    for (const t of T) {
      sorok.push({ csoport: `${t.nev} – ${t.db} jegy · átlag: ${this.szam(t.atlag)}${t.szazalek != null ? ` · ${this.szam(t.szazalek, 1)}%` : ''}` });
      for (const j of t.jegyek) {
        sorok.push([j.datum, { t: j.tanulo, fej: true }, this.egyseg(j), this.jegySzoveg(j), this.modSzoveg(j),
          j.suly == null ? '' : `${Number(j.suly) / 100}×`, j.tema || '']);
      }
    }
    const fajlnev = `${this.fajlTorzs()}.pdf`;
    await this.data.showLoading('PDF készítése...');
    try {
      await this.data.riportPdf({
        intezmeny: this.intezmeny,
        cim: 'Jegyek tanáronként',
        cimKieg: tanarNev,
        alcim: idoszak,
        adatsav: [['Tanár', tanarNev], ['Időszak', idoszak], ['Jegyek', String(this.ossz.db)],
          ['Átlag (évközi érdemjegyek, súlyozva)', this.szam(this.ossz.atlag)]],
        lapmeret: 'A4', tajolas: 'fekvo', betumeret: 8.5,
        tablak: [
          {
            cim: 'Tantárgyanként', osszegzes: `${T.length} tantárgy`,
            oszlopok: [{ nev: 'Tantárgy', szelesseg: '24%' }, { nev: 'Osztály / csoport', szelesseg: '36%' },
              { nev: 'Jegyek', igazitas: 'jobb', szelesseg: '8%' }, { nev: 'Tanulók', igazitas: 'jobb', szelesseg: '8%' },
              { nev: 'Átlag', igazitas: 'jobb', szelesseg: '8%' }, { nev: '% átlag', igazitas: 'jobb', szelesseg: '8%' },
              { nev: 'Utolsó jegy', szelesseg: '8%' }],
            sorok: T.map((t) => [{ t: t.nev, fej: true }, t.egysegek, String(t.db), String(t.tanulok), this.szam(t.atlag),
              t.szazalek == null ? '—' : `${this.szam(t.szazalek, 1)}%`, t.utolso]),
          },
          {
            cim: 'Jegyek', osszegzes: `${this.ossz.db} jegy`, ures: 'Nincs jegy az időszakban.',
            oszlopok: [{ nev: 'Dátum', szelesseg: '9%' }, { nev: 'Tanuló', szelesseg: '20%' }, { nev: 'Osztály / csoport', szelesseg: '16%' },
              { nev: 'Jegy', igazitas: 'kozep', szelesseg: '9%' }, { nev: 'Értékelés módja', szelesseg: '20%' },
              { nev: 'Súly', igazitas: 'jobb', szelesseg: '5%' }, { nev: 'Téma / megjegyzés', szelesseg: '21%' }],
            sorok,
          },
        ],
        lablec: { kelt: true },
        fajlnev,
      }, fajlnev, 'letolt');
    } catch (e: any) {
      this.data.sendToast(e?.message || 'Nem sikerült elkészíteni a PDF-et.', 'error');
    } finally { await this.data.hideLoading(); }
  }

  csv() {
    if (!this.adat) return;
    const cella = (v: any) => { const s = String(v ?? ''); return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const ki = [['Tanár', 'Dátum', 'Tanuló', 'Oktatási azonosító', 'Osztály', 'Csoport', 'Tantárgy', 'Jegy', 'Típus', 'Értékelés módja', 'Súly', 'Téma / megjegyzés'].join(';')];
    for (const t of this.csoportok) {
      for (const j of t.jegyek) {
        ki.push([this.adat.tanar?.nev, j.datum, j.tanulo, j.oktazon, j.osztaly, j.csoport || '', j.tantargy, this.jegySzoveg(j),
          j.tipus || '', j.modja || '', j.suly == null ? '' : String(Number(j.suly) / 100).replace('.', ','), j.tema || ''].map(cella).join(';'));
      }
    }
    const blob = new Blob([`﻿${ki.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${this.fajlTorzs()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
